/**
 * THE TEAM — the one roster the whole cockpit plans against.
 *
 * It replaces two things that used to sit in different tabs and disagree: the "add recruiters /
 * add interns" counters here (which moved a cluster total nobody could trace back to a person) and
 * Role Prioritisation's per-person table (which lived in `localStorage`, so every colleague saw a
 * different team).
 *
 * ⚠️ EDITS ARE LOCAL UNTIL SAVED, and that is what makes one control serve both jobs. Changing a
 * row recomputes the coverage above immediately, so "what if we hire one more into Operations" is
 * just adding the row and looking — and nothing reaches anyone else until Save. A scenario you
 * decide against is a Discard, not a second mechanism.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Trash2 } from 'lucide-react';
import { cn } from '../../../../../shared';
import {
  CLUSTER_KEYS,
  UNASSIGNED,
  WORKING_MODELS,
  isIntern,
  isPlanned,
  newMember,
  type MemberCluster,
  type TeamMember,
} from '../../../types/taTeam';
import { Section, WhatThisCounts } from '../tracking/trackingUi';

const FIELD =
  'h-8 rounded-md border border-solid border-gray-300 bg-white px-2 text-[13px] text-gray-900 ' +
  'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500';

const TH = 'px-2 py-2 text-left text-xs font-medium text-gray-500';
const TD = 'border-t border-gray-100 px-2 py-1.5 text-sm';

export function TeamAndModel({
  members,
  saved,
  savedBy,
  dirty,
  busy,
  readError,
  error,
  fullWeekHours,
  onChange,
  onSave,
  onDiscard,
}: {
  members: TeamMember[];
  /** Whether the server has a roster at all. False means this is the seeded starting point. */
  saved: boolean;
  savedBy: string | null;
  dirty: boolean;
  busy: boolean;
  /** Why the saved roster could not be READ. Save is blocked while this is set — see below. */
  readError: string | null;
  error: string | null;
  fullWeekHours: number;
  onChange: (next: TeamMember[]) => void;
  onSave: () => void;
  onDiscard: () => void;
}) {
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const week = fullWeekHours > 0 ? fullWeekHours : 40;

  const write = (id: string, patch: Partial<TeamMember>) =>
    onChange(members.map((m) => (m.id === id ? { ...m, ...patch } : m)));

  const planned = members.filter((m) => isPlanned(m) && m.hours > 0);
  const recruiterFte = planned.filter((m) => !isIntern(m)).reduce((s, m) => s + m.hours, 0) / week;
  const internFte = planned.filter(isIntern).reduce((s, m) => s + m.hours, 0) / week;
  const unassigned = planned.filter((m) => m.cluster === UNASSIGNED).length;

  return (
    <Section
      title="The team"
      aside={
        <span className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
          <span className="tabular-nums">
            <b className="font-semibold text-gray-900">{recruiterFte.toFixed(2)}</b> recruiter FTE
            {internFte > 0 ? (
              <>
                {' · '}
                <b className="font-semibold text-gray-900">{internFte.toFixed(2)}</b> intern FTE
              </>
            ) : null}
          </span>
          <WhatThisCounts
            label="The team"
            why={`Every tab plans against this one roster — capacity here, Role Prioritisation's hours, the cluster each recruiter carries. FTE is contracted hours ÷ ${week} h. Someone unticked under "Counts" stays on the team with their hours in no capacity figure, which is how the Head of TA sits here without her time being planned as hiring capacity.`}
          />
        </span>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr>
              <th className={TH}>Person</th>
              <th className={TH}>Role</th>
              <th className={TH}>Cluster</th>
              <th className={TH}>Working model</th>
              <th className={`${TH} text-center`}>
                Counts{' '}
                <WhatThisCounts
                  label="Counts"
                  why="Whether these hours are planned as hiring capacity. Untick for someone who is on the team but whose time is not part of the hiring plan — their hours then sit in no capacity figure until they choose to offer them."
                />
              </th>
              <th className={`${TH} text-right`}>FTE</th>
              <th className={TH} />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const off = !isPlanned(m);
              return (
                <tr key={m.id} className={cn(off && 'bg-gray-50')}>
                  <td className={TD}>
                    <input
                      value={m.name}
                      aria-label={`Name of ${m.name}`}
                      onChange={(e) => write(m.id, { name: e.target.value })}
                      className={cn(FIELD, 'w-[168px] font-medium')}
                    />
                  </td>
                  <td className={TD}>
                    <input
                      value={m.role}
                      aria-label={`Role of ${m.name}`}
                      onChange={(e) => write(m.id, { role: e.target.value })}
                      className={cn(FIELD, 'w-[132px] text-gray-600')}
                    />
                  </td>
                  <td className={TD}>
                    <select
                      value={m.cluster}
                      aria-label={`Cluster for ${m.name}`}
                      onChange={(e) => write(m.id, { cluster: e.target.value as MemberCluster })}
                      className={cn(FIELD, 'w-[148px]')}
                    >
                      {[...CLUSTER_KEYS, UNASSIGNED].map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={TD}>
                    {/*
                      A list, not a free number: the models the team actually works are a short
                      fixed set, and a typed 37 is far more often a typo than a contract.
                    */}
                    <select
                      value={String(m.hours)}
                      aria-label={`Working model for ${m.name}`}
                      onChange={(e) => write(m.id, { hours: Number(e.target.value) || 0 })}
                      className={cn(FIELD, 'w-[176px]')}
                    >
                      {WORKING_MODELS.some((w) => w.hours === m.hours) ? null : (
                        <option value={String(m.hours)}>{m.hours} h</option>
                      )}
                      {WORKING_MODELS.map((w) => (
                        <option key={w.hours} value={String(w.hours)}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={`${TD} text-center`}>
                    {/* An explicit id: the atom otherwise derives one from `label`/`value`, and
                        with neither set every row would render id="undefined-undefined". */}
                    <Checkbox
                      id={`counts-${m.id}`}
                      checked={isPlanned(m)}
                      aria-label={`Count ${m.name} as hiring capacity`}
                      onCheckedChange={(v) => write(m.id, { countsTowardHiring: v === true })}
                    />
                  </td>
                  <td className={`${TD} text-right tabular-nums text-gray-600`}>
                    {off ? '—' : (m.hours / week).toFixed(2)}
                  </td>
                  <td className={`${TD} text-right`}>
                    {confirmRemove === m.id ? (
                      <span className="flex items-center justify-end gap-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            onChange(members.filter((x) => x.id !== m.id));
                            setConfirmRemove(null);
                          }}
                        >
                          Remove
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(null)}>
                          Keep
                        </Button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Remove ${m.name}`}
                        onClick={() => setConfirmRemove(m.id)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-rose-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onChange([...members, newMember('recruiter')])}
        >
          Add a recruiter
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onChange([...members, newMember('intern')])}
        >
          Add an intern
        </Button>

        <span className="ml-auto flex flex-wrap items-center gap-2">
          {/*
            Three states, not two. "Nothing is saved" is NOT the same as "you have edits": the first
            is a team nobody has confirmed, which is exactly the situation the shared roster exists
            to end, and calling it "unsaved changes" would hide it behind a word that sounds routine.
          */}
          {/*
            ⚠️ A FAILED READ IS NOT AN EMPTY ONE, and Save is refused rather than offered. With the
            error swallowed, a transient outage showed the seeded names under "nobody has saved a
            roster yet" — and one Save would have replaced the real shared roster with a team
            nobody assembled.
          */}
          {readError ? (
            <span className="text-xs font-medium text-rose-700">
              The saved team could not be read, so this is not it — reload before changing anything
            </span>
          ) : !saved ? (
            <span className="text-xs font-medium text-amber-700">
              Nobody has saved a roster yet — this is the starting point, not a saved team
            </span>
          ) : dirty ? (
            <>
              <span className="text-xs font-medium text-amber-700">Unsaved — only you see this</span>
              <Button variant="ghost" size="sm" disabled={busy} onClick={onDiscard}>
                Discard
              </Button>
            </>
          ) : (
            <span className="text-xs text-gray-500">
              Saved{savedBy ? ` by ${savedBy.split('@')[0]}` : ''} · every tab uses this
            </span>
          )}
          {dirty && !readError ? (
            <Button variant="primary" size="sm" disabled={busy} onClick={onSave}>
              {busy ? 'Saving…' : 'Save the team'}
            </Button>
          ) : null}
        </span>
      </div>

      {error ? <p className="mt-2 text-xs text-rose-700">{error}</p> : null}

      {unassigned > 0 ? (
        <p className="mt-2 text-xs text-amber-700">
          {unassigned} {unassigned === 1 ? 'person carries' : 'people carry'} no cluster, so their
          hours are in nobody&apos;s capacity. Spreading them would invent capacity somewhere.
        </p>
      ) : null}
    </Section>
  );
}
